import {
  type ApiResponse,
  type PaginatedResponse,
  idempotencyHeader
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  CreateSponsorshipCampaignInput,
  PublicSponsorshipCampaign,
  SetPlaceBeneficiaryInput,
  SponsoredPlace,
  SponsoredPlaceWithJob,
  SponsorshipCampaign,
  SponsorshipCampaignGroupSummary,
  SponsorshipCampaignStatus,
  SponsorshipCheckoutInput,
  SponsorshipCheckoutResult,
  SponsorshipPlaceStatus,
  UpdateSponsorshipCampaignInput
} from './types/sponsorships.types';

// Sponsorship campaigns (BMS feature 0817). Deliberately separate from the
// donation campaigns in campaigns.service.ts: these sell places as package
// sales, never donations.

const withQuery = (path: string, params: Record<string, string | number | undefined>): string => {

  const query = new URLSearchParams();

  for (const [ key, value ] of Object.entries(params)) {

    if (value !== undefined) {

      query.set(key, String(value));
    }
  }

  return query.size > 0 ? `${ path }?${ query.toString() }` : path;
};

// ── Public (the /support pages) ──────────────────────────────────────────────

/** Public - no auth. Active, in-date campaigns with their computed price. */
export async function listLiveSponsorshipCampaigns(): Promise<ApiResponse<PublicSponsorshipCampaign[]>> {

  return apiRequest<ApiResponse<PublicSponsorshipCampaign[]>>('/sponsorship-campaigns/live');
}

/** Public - no auth. 404 unless the campaign is live. */
export async function getLiveSponsorshipCampaignByKey(
  campaignKey: string
): Promise<ApiResponse<PublicSponsorshipCampaign>> {

  return apiRequest<ApiResponse<PublicSponsorshipCampaign>>(`/sponsorship-campaigns/by-key/${ encodeURIComponent(campaignKey) }`);
}

/** Public, reCAPTCHA-gated (action 'sponsorship_checkout'). An
 * Idempotency-Key replays a retry instead of creating a second sale. */
export async function checkoutSponsorship(
  campaignKey: string,
  input: SponsorshipCheckoutInput,
  idempotencyKey?: string
): Promise<ApiResponse<SponsorshipCheckoutResult>> {

  return apiRequest<ApiResponse<SponsorshipCheckoutResult>>(
    `/sponsorship-campaigns/${ encodeURIComponent(campaignKey) }/checkout`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: idempotencyHeader(idempotencyKey)
    }
  );
}

// ── Admin (admin or sponsorships:manage) ─────────────────────────────────────

export interface ListSponsorshipCampaignsParams {
  status?: SponsorshipCampaignStatus
  limit?: number
  offset?: number
}

export async function listSponsorshipCampaigns(
  params: ListSponsorshipCampaignsParams,
  token: string
): Promise<PaginatedResponse<SponsorshipCampaign>> {

  return apiRequest<PaginatedResponse<SponsorshipCampaign>>(withQuery('/sponsorship-campaigns', {
    status: params.status,
    limit: params.limit,
    offset: params.offset
  }), undefined, token);
}

export async function getSponsorshipCampaignById(
  id: number,
  token: string
): Promise<ApiResponse<SponsorshipCampaign>> {

  return apiRequest<ApiResponse<SponsorshipCampaign>>(`/sponsorship-campaigns/${ id }`, undefined, token);
}

/** Seats funded per campaign in a group, and in total (the revision
 * go/no-go count). */
export async function getSponsorshipCampaignGroupSummary(
  groupKey: string,
  token: string
): Promise<ApiResponse<SponsorshipCampaignGroupSummary>> {

  return apiRequest<ApiResponse<SponsorshipCampaignGroupSummary>>(
    `/sponsorship-campaigns/groups/${ encodeURIComponent(groupKey) }/summary`,
    undefined,
    token
  );
}

export async function createSponsorshipCampaign(
  input: CreateSponsorshipCampaignInput,
  token: string
): Promise<ApiResponse<SponsorshipCampaign>> {

  return apiRequest<ApiResponse<SponsorshipCampaign>>(
    '/sponsorship-campaigns',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateSponsorshipCampaign(
  id: number,
  input: UpdateSponsorshipCampaignInput,
  token: string
): Promise<ApiResponse<SponsorshipCampaign>> {

  return apiRequest<ApiResponse<SponsorshipCampaign>>(
    `/sponsorship-campaigns/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

/** Status only - the same PUT, kept as its own call so a list-row action
 * cannot accidentally send a whole stale form. */
export async function setSponsorshipCampaignStatus(
  id: number,
  campaignStatus: SponsorshipCampaignStatus,
  token: string
): Promise<ApiResponse<SponsorshipCampaign>> {

  return updateSponsorshipCampaign(id, {
    campaignStatus
  }, token);
}

/** Only a campaign nobody has been invoiced for; billing answers 409
 * otherwise (close it instead). */
export async function deleteSponsorshipCampaign(
  id: number,
  token: string
): Promise<void> {

  await apiRequest<unknown>(
    `/sponsorship-campaigns/${ id }`,
    {
      method: 'DELETE'
    },
    token
  );
}

export interface ListSponsoredPlacesParams {
  status?: SponsorshipPlaceStatus
  limit?: number
  offset?: number
}

export async function listSponsoredPlaces(
  campaignId: number,
  params: ListSponsoredPlacesParams,
  token: string
): Promise<PaginatedResponse<SponsoredPlace>> {

  return apiRequest<PaginatedResponse<SponsoredPlace>>(withQuery(`/sponsorship-campaigns/${ campaignId }/places`, {
    status: params.status,
    limit: params.limit,
    offset: params.offset
  }), undefined, token);
}

/** Assign (or, with both ids null, clear) the learner a place goes to.
 * Assigning asks EMS for the place's job; the result says whether it went. */
export async function setSponsoredPlaceBeneficiary(
  placeId: number,
  input: SetPlaceBeneficiaryInput,
  token: string
): Promise<ApiResponse<SponsoredPlaceWithJob>> {

  return apiRequest<ApiResponse<SponsoredPlaceWithJob>>(
    `/sponsorship-places/${ placeId }/beneficiary`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function setSponsoredPlaceStatus(
  placeId: number,
  status: 'DELIVERED' | 'REFUNDED',
  token: string
): Promise<ApiResponse<SponsoredPlace>> {

  return apiRequest<ApiResponse<SponsoredPlace>>(
    `/sponsorship-places/${ placeId }/status`,
    {
      method: 'PUT',
      body: JSON.stringify({
        status
      })
    },
    token
  );
}

/** Re-send the place's job to EMS (after an EMS outage, say). */
export async function dispatchSponsoredPlaceJob(
  placeId: number,
  token: string
): Promise<ApiResponse<SponsoredPlaceWithJob>> {

  return apiRequest<ApiResponse<SponsoredPlaceWithJob>>(
    `/sponsorship-places/${ placeId }/dispatch-job`,
    {
      method: 'POST'
    },
    token
  );
}
